/**
 * Demo account on request. Clients start with a live account only; Demo in the top bar (computer only)
 * opens a notice that demo is not real money and is for education only, and accepting it sends a
 * request to the client's account manager in the CRM. Until it is approved Demo shows a lock, or a
 * dot while the request waits. On a phone the account menu shows the demo row, greyed out.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { DEMO_NOTICE_VERSION, fmtUsd, type AccountSummary, type Me } from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { useIsDesktop } from '../lib/media';
import { loadMe, setAccountKind, useAccountKind, useMe } from '../stores/session';
import { cx, Icon, Modal } from './ui';

type DemoState = 'none' | 'pending' | 'declined' | 'open';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "12 Oct, 14:05" in the viewer's time. */
function shortWhen(iso: string) {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function demoState(me: Me): DemoState {
  if (me.accounts.some((a) => a.kind === 'demo')) return 'open';
  return me.demo?.request?.status ?? 'none';
}

/** Live / Demo switch in the top bar: computers only, for clients. */
export function DemoSwitch() {
  const me = useMe();
  const desktop = useIsDesktop();
  const kind = useAccountKind();
  const [modal, setModal] = useState(false);
  const [pop, setPop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pop) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setPop(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPop(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pop]);
  if (!me || !desktop) return null;
  const st = demoState(me);
  // Staff and staff-made users without a live account keep the old behaviour: no switch without a choice.
  if (me.role !== 'user' && st !== 'open') return null;
  const onDemo = st === 'open' && kind === 'demo';
  const clickDemo = () => {
    if (st === 'open') return setAccountKind('demo');
    if (st === 'none') return setModal(true);
    setPop((p) => !p);
  };
  const seg = (on: boolean) =>
    cx('flex h-[28px] items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold sm:px-3', on ? 'bg-nav-pill text-nav-fg' : 'text-nav-muted hover:text-nav-fg');
  return (
    <div ref={ref} className="relative">
      <div role="group" aria-label="Account mode" className="flex rounded-full border border-nav-line p-[3px]">
        <button type="button" aria-pressed={!onDemo} onClick={() => setAccountKind('live')} className={seg(!onDemo)}>
          Live
        </button>
        <button
          type="button"
          aria-pressed={onDemo}
          aria-haspopup={st === 'open' ? undefined : 'dialog'}
          aria-expanded={st === 'pending' || st === 'declined' ? pop : undefined}
          title={st === 'pending' ? 'Demo account: waiting for approval' : st === 'open' ? undefined : 'Request a demo account'}
          onClick={clickDemo}
          className={seg(onDemo)}
        >
          {(st === 'none' || st === 'declined') && <Icon name="lock" size={11} strokeWidth={2.5} />}
          {st === 'pending' && <span aria-hidden="true" className="size-1.5 rounded-full bg-warn" />}
          Demo
        </button>
      </div>
      {pop && st === 'pending' && <PendingPanel me={me} onDone={() => setPop(false)} />}
      {pop && st === 'declined' && (
        <DeclinedPanel
          me={me}
          onAgain={() => {
            setPop(false);
            setModal(true);
          }}
        />
      )}
      {modal && <DemoRequestModal balance={me.demo.balance} onClose={() => setModal(false)} />}
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <div className="shadow-pop absolute top-11 right-0 z-40 flex w-[300px] flex-col gap-2 rounded-xl border border-line bg-panel p-3.5 text-fg">{children}</div>;
}

function PendingPanel({ me, onDone }: { me: Me; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancel = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.del('/api/accounts/demo/request');
      await loadMe();
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not cancel the request. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel>
      <span className="w-fit rounded-lg bg-warn-bg px-2 py-0.5 text-[12px] font-semibold text-warn">Waiting for approval</span>
      <div className="text-[15px] font-semibold">Demo account requested</div>
      <p className="m-0 text-[13px] leading-snug text-muted">
        Sent on {shortWhen(me.demo.request!.createdAt)}. Your account manager is reviewing your request. We'll notify you as soon as your demo account is ready.
      </p>
      <div className="my-0.5 h-px bg-line" />
      {err && <p className="m-0 text-[12.5px] text-down">{err}</p>}
      <button type="button" disabled={busy} onClick={() => void cancel()} className="w-fit text-[13px] font-semibold text-down disabled:opacity-50">
        Cancel request
      </button>
    </Panel>
  );
}

function DeclinedPanel({ me, onAgain }: { me: Me; onAgain: () => void }) {
  const retryAt = me.demo.request!.retryAt;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const ready = !retryAt || now >= new Date(retryAt).getTime();
  return (
    <Panel>
      <span className="w-fit rounded-lg bg-down-bg px-2 py-0.5 text-[12px] font-semibold text-down">Not approved</span>
      <div className="text-[15px] font-semibold">Demo account request</div>
      <p className="m-0 text-[13px] leading-snug text-muted">
        Your request wasn't approved this time. Your account manager will be in touch.{retryAt && !ready ? ` You can ask again from ${shortWhen(retryAt)}.` : ' You can ask again now.'}
      </p>
      <button type="button" disabled={!ready} onClick={onAgain} className="h-9 rounded-full bg-pill text-[13px] font-semibold text-fg disabled:opacity-50">
        Request again
      </button>
    </Panel>
  );
}

const POINTS = ['It is not real money. It cannot be deposited or withdrawn.', 'Profits and losses on demo are not paid out or charged.', 'It is for education and practice only, on a computer.'];

/** The notice shown above the demo account; accepting it sends the request to the account manager. */
function DemoRequestModal({ balance, onClose }: { balance: string; onClose: () => void }) {
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post('/api/accounts/demo/request', { accepted: true, noticeVersion: DEMO_NOTICE_VERSION });
      setSent(true);
      void loadMe().catch(() => {});
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send the request. Try again.');
      if (e instanceof ApiError && (e.code === 'demo_request_pending' || e.code === 'demo_exists')) void loadMe().catch(() => {});
    } finally {
      setBusy(false);
    }
  };
  if (sent)
    return (
      <Modal open onClose={onClose} title="Request sent" width={440}>
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="grid size-12 place-items-center rounded-full bg-up-bg text-up">
            <Icon name="check" size={22} strokeWidth={2.5} />
          </span>
          <p className="m-0 max-w-[340px] text-[14px] leading-snug text-muted">
            Your account manager will review your request. We'll notify you as soon as your demo account is ready, usually within one working day.
          </p>
        </div>
        <button type="button" onClick={onClose} className="h-11 rounded-full bg-fg text-[15px] font-semibold text-bg">
          Done
        </button>
      </Modal>
    );
  return (
    <Modal open onClose={onClose} title="Request a demo account" width={440}>
      <div className="flex items-start gap-3 rounded-xl bg-warn-bg p-3.5 text-warn">
        <Icon name="info" size={18} className="mt-px shrink-0" />
        <div className="min-w-0">
          <div className="text-[14px] font-semibold">Not real money</div>
          <div className="text-[13px] leading-snug">A demo account is for education purposes only.</div>
        </div>
      </div>
      <p className="m-0 text-[14px] leading-snug text-muted">
        Practise trading with <span className="num font-semibold text-fg">{fmtUsd(balance)}</span> in virtual funds before you trade with real money.
      </p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {POINTS.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-[13.5px] leading-snug">
            <Icon name="check" size={15} strokeWidth={2.5} className="mt-px shrink-0 text-muted" />
            {p}
          </li>
        ))}
      </ul>
      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-line bg-raised p-3 text-[13.5px] leading-snug">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 size-4 shrink-0" />
        I understand that the demo account is not real money and is for education purposes only.
      </label>
      <p className="m-0 text-[12.5px] leading-snug text-muted">Your account manager reviews each request. We'll notify you when your demo account is ready.</p>
      {err && <p className="m-0 text-[13px] text-down">{err}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full bg-pill text-[15px] font-semibold text-fg">
          Not now
        </button>
        <button type="button" disabled={!ok || busy} onClick={() => void send()} className="h-11 flex-1 rounded-full bg-fg text-[15px] font-semibold text-bg disabled:opacity-40">
          Send request
        </button>
      </div>
    </Modal>
  );
}

/** Thin bar under the top navigation while the demo account is selected. */
export function DemoBar() {
  const kind = useAccountKind();
  const me = useMe();
  if (kind !== 'demo' || !me) return null;
  return (
    <div className="flex items-center justify-center gap-x-3 gap-y-1 bg-warn-bg px-4 py-2 text-center text-[13px] text-warn max-sm:flex-col">
      <span className="flex items-center gap-2 font-medium">
        <Icon name="info" size={13} />
        Demo account: virtual funds for education only. This is not real money.
      </span>
      <button type="button" onClick={() => setAccountKind('live')} className="font-semibold text-fg underline underline-offset-4">
        Switch to live
      </button>
    </div>
  );
}

const SUB: Record<DemoState, string> = { none: 'Request on a computer', pending: 'Waiting for approval', declined: 'Not approved', open: 'Available on a computer' };

/** Phone account menu: the demo account is shown with its state but can't be picked. */
export function MobileDemoRow({ me }: { me: Me }) {
  const st = demoState(me);
  if (me.role !== 'user' && st !== 'open') return null;
  const demo: AccountSummary | undefined = me.accounts.find((a) => a.kind === 'demo');
  return (
    <div aria-disabled="true" className="flex items-center gap-3 rounded-[18px] px-3.5 py-3 text-left text-fg opacity-60">
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-bg text-accent-fg">
        <Icon name={st === 'none' || st === 'declined' ? 'lock' : 'reset'} size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-semibold">Demo account</span>
        <span className={cx('block truncate text-[14px]', st === 'pending' ? 'text-warn' : st === 'declined' ? 'text-down' : 'text-muted')}>{SUB[st]}</span>
      </span>
      {demo && <span className="tnum text-right text-[16px] font-semibold text-muted">{fmtUsd(demo.equity)}</span>}
    </div>
  );
}
